import { useState, useEffect, useCallback, useRef } from "react";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  runTransaction,
  increment,
} from "firebase/firestore";
import { db, track } from "./firebase";

function eloUpdate(winnerElo, loserElo, K = 32) {
  const expectedWinner = 1 / (1 + Math.pow(10, (loserElo - winnerElo) / 400));
  return {
    newWinner: Math.round(winnerElo + K * (1 - expectedWinner)),
    newLoser: Math.round(loserElo + K * (0 - (1 - expectedWinner))),
  };
}

function pairKey(uid, w1, w2) {
  const sorted = [w1, w2].sort();
  return `${uid}_${sorted[0]}_${sorted[1]}`;
}

async function findPair(words, uid, seenPairs) {
  if (!words.length || !uid) return null;

  const sorted = [...words].sort((a, b) => a.comparisons - b.comparisons);
  const candidateCount = Math.max(10, Math.floor(sorted.length * 0.4));
  const candidates = sorted.slice(0, candidateCount);

  // Try in-memory seen set first (no Firestore reads)
  for (let attempt = 0; attempt < 200; attempt++) {
    const i = Math.floor(Math.random() * candidates.length);
    let j = Math.floor(Math.random() * candidates.length);
    while (j === i) j = Math.floor(Math.random() * candidates.length);
    const w1 = candidates[i];
    const w2 = candidates[j];
    const key = pairKey(uid, w1.id, w2.id);
    if (!seenPairs.has(key)) {
      // Only hit Firestore if not in memory cache
      const voteDoc = await getDoc(doc(db, "votes", key));
      if (!voteDoc.exists()) {
        return { pair: [w1, w2], key };
      }
      seenPairs.add(key); // cache it
    }
  }

  // Fallback: fully random unseen pair
  const i = Math.floor(Math.random() * words.length);
  let j = Math.floor(Math.random() * words.length);
  while (j === i) j = Math.floor(Math.random() * words.length);
  return { pair: [words[i], words[j]], key: pairKey(uid, words[i].id, words[j].id) };
}

export function useVoting(words, uid) {
  const [pair, setPair] = useState(null);
  const [nextPair, setNextPair] = useState(null);
  const [phase, setPhase] = useState("show");
  const [voteCount, setVoteCount] = useState(0);
  const seenPairs = useRef(new Set());
  const voting = useRef(false); // double-click guard
  const pairShownAt = useRef(null); // for timing analytics

  const preloadNext = useCallback(
    async (currentWords, currentUid) => {
      const result = await findPair(currentWords, currentUid, seenPairs.current);
      if (result) setNextPair(result.pair);
    },
    []
  );

  const loadFirstPair = useCallback(async () => {
    if (!words.length || !uid) return;
    const result = await findPair(words, uid, seenPairs.current);
    if (result) {
      setPair(result.pair);
      setPhase("show");
      pairShownAt.current = Date.now();
      preloadNext(words, uid);
    }
  }, [words, uid, preloadNext]);

  useEffect(() => {
    if (words.length && uid && !pair) {
      loadFirstPair();
    }
  }, [words, uid]); // eslint-disable-line

  const advance = useCallback(() => {
    setPhase("exit");
    setTimeout(() => {
      if (nextPair) {
        setPair(nextPair);
        setNextPair(null);
        setPhase("show");
        pairShownAt.current = Date.now();
        preloadNext(words, uid);
      } else {
        // Fallback if preload wasn't ready
        loadFirstPair();
      }
    }, 350);
  }, [nextPair, words, uid, preloadNext, loadFirstPair]);

  const vote = useCallback(
    async (winnerId) => {
      if (!pair || !uid || voting.current) return;
      voting.current = true;

      const [w1, w2] = pair;
      const winner = w1.id === winnerId ? w1 : w2;
      const loser = w1.id === winnerId ? w2 : w1;
      const key = pairKey(uid, w1.id, w2.id);
      const timeSpent = pairShownAt.current ? Date.now() - pairShownAt.current : null;

      seenPairs.current.add(key);
      setVoteCount((c) => c + 1);
      advance();

      // Use transaction so Elo is always based on latest values
      try {
        await runTransaction(db, async (tx) => {
          const winnerSnap = await tx.get(doc(db, "words", winner.id));
          const loserSnap = await tx.get(doc(db, "words", loser.id));
          const wElo = winnerSnap.data().elo;
          const lElo = loserSnap.data().elo;
          const { newWinner, newLoser } = eloUpdate(wElo, lElo);

          tx.update(doc(db, "words", winner.id), {
            elo: newWinner,
            wins: increment(1),
            comparisons: increment(1),
          });
          tx.update(doc(db, "words", loser.id), {
            elo: newLoser,
            losses: increment(1),
            comparisons: increment(1),
          });
          tx.set(doc(db, "votes", key), {
            uid,
            winner: winner.id,
            loser: loser.id,
            timestamp: Date.now(),
          });
          tx.update(doc(db, "users", uid), { votesCount: increment(1) });
        });

        track("vote", {
          winner: winner.id,
          loser: loser.id,
          time_spent_ms: timeSpent,
        });
      } catch (err) {
        console.error("Vote failed", err);
      } finally {
        voting.current = false;
      }
    },
    [pair, uid, advance]
  );

  const skip = useCallback(async () => {
    if (!pair || !uid || voting.current) return;
    voting.current = true;

    const [w1, w2] = pair;
    const key = pairKey(uid, w1.id, w2.id);
    seenPairs.current.add(key);
    advance();

    track("skip", { word_a: w1.id, word_b: w2.id });

    try {
      await Promise.all([
        updateDoc(doc(db, "words", w1.id), { skips: increment(1) }),
        updateDoc(doc(db, "words", w2.id), { skips: increment(1) }),
      ]);
    } finally {
      voting.current = false;
    }
  }, [pair, uid, advance]);

  return { pair, vote, skip, phase, voteCount };
}
